import { useMemo, useState } from 'react';
import { COLOR_RULES } from '@/lib/store-template-kinds';
import { filterPickerCards, type LineupPickerCard, type PrintLocation } from '@/lib/lineup-thumbs';

export function GarmentThumb({
  src,
  alt = '',
  className = 'h-16 w-16',
}: {
  src: string;
  alt?: string;
  className?: string;
}) {
  if (!src) {
    return <div className={`shrink-0 rounded-sm bg-bg ${className}`} aria-hidden />;
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`shrink-0 rounded-sm bg-white object-contain ${className}`}
    />
  );
}

export function LocationChips({
  locations,
  empty = 'No logo locations in catalog',
  selected,
  onSelect,
}: {
  locations: PrintLocation[];
  empty?: string;
  selected?: string;
  onSelect?: (position: string) => void;
}) {
  if (!locations.length) {
    return <span className="text-[10px] text-subtle">{empty}</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {locations.map((l) => {
        const on = !!selected && selected === l.position;
        const cls = `rounded-sm px-1.5 py-0.5 text-[10px] ${on ? 'bg-primary text-white' : 'bg-bg text-text/80'} ${onSelect ? 'cursor-pointer' : ''}`;
        const label = `${l.label}${l.size ? ` ${l.size}` : ''}`;
        if (onSelect) {
          return (
            <button
              key={`${l.position}-${l.code}`}
              type="button"
              onClick={() => onSelect(l.position)}
              className={cls}
              title={l.size ? `${l.label} · ${l.size} print` : l.label}
            >
              {label}
            </button>
          );
        }
        return (
          <span key={`${l.position}-${l.code}`} className={cls} title={l.size ? `${l.label} · ${l.size} print` : l.label}>
            {label}
          </span>
        );
      })}
    </div>
  );
}

function Fact({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.04em] text-subtle">{label}</div>
      <div className="text-[11.5px] text-text/90">{value}</div>
    </div>
  );
}

function cardInLineup(card: LineupPickerCard, inLineup: Set<string>): boolean {
  return [card.styleNumber, ...card.aliases].some((a) => inLineup.has(a.toUpperCase()));
}

export default function LineupGarmentPicker({
  cards,
  inLineup,
  selectedStyle,
  onSelectStyle,
  newColors,
  onColors,
  onAdd,
  onQuickAdd,
  typedStyle,
  onTypedStyle,
  canAdd,
  addDisabledReason,
}: {
  cards: LineupPickerCard[];
  inLineup: Set<string>;
  selectedStyle: string;
  onSelectStyle: (styleNumber: string) => void;
  newColors: Record<string, boolean>;
  onColors: (next: Record<string, boolean>) => void;
  onAdd: () => void;
  onQuickAdd: (styleNumber: string) => void;
  typedStyle: string;
  onTypedStyle: (styleNumber: string) => void;
  canAdd: boolean;
  addDisabledReason?: string;
}) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [hideAdded, setHideAdded] = useState(true);
  const categories = useMemo(
    () => [...new Set(cards.map((c) => c.category).filter(Boolean))].sort(),
    [cards],
  );
  const visible = useMemo(() => {
    const filtered = filterPickerCards(cards, query, category);
    return hideAdded ? filtered.filter((c) => !cardInLineup(c, inLineup)) : filtered;
  }, [cards, query, category, hideAdded, inLineup]);
  const selected = cards.find((c) => {
    const needle = selectedStyle.toUpperCase();
    return c.styleNumber.toUpperCase() === needle || c.aliases.some((a) => a.toUpperCase() === needle);
  }) || null;
  const alreadyIn = selected ? cardInLineup(selected, inLineup) : inLineup.has(selectedStyle.toUpperCase());

  return (
    <div className="rounded-md border border-dashed border-border p-3">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="text-[11px] font-semibold text-text/80">Catalog — click to add</div>
          <div className="text-[10px] text-subtle">Photo, logo locations, and sizes are from the supplier catalog. Designs bind later on Assign to Products.</div>
        </div>
        <label className="flex items-center gap-1 text-[11px] text-muted">
          <input type="checkbox" checked={hideAdded} onChange={(e) => setHideAdded(e.target.checked)} />
          Hide already in lineup
        </label>
      </div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, style #, brand, or logo location…"
          className="h-7 min-w-[200px] flex-1 rounded-sm border border-border bg-white px-1.5 text-[11px]"
        />
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-7 rounded-sm border border-border bg-white px-1.5 text-[11px]"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <div className="mb-3 grid max-h-[420px] gap-2 overflow-y-auto pr-1" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))' }}>
        {visible.map((c) => {
          const added = cardInLineup(c, inLineup);
          const active = selected?.key === c.key;
          return (
            <div
              key={c.key}
              className={`rounded-md border p-1.5 text-left ${active ? 'border-primary ring-2 ring-primary/20' : 'border-border'} ${added ? 'opacity-70' : ''}`}
            >
              <button type="button" onClick={() => onSelectStyle(c.styleNumber)} className="w-full text-left">
                <div className="aspect-square w-full overflow-hidden rounded-sm bg-bg">
                  {c.image
                    ? <img src={c.image} alt="" loading="lazy" className="h-full w-full bg-white object-contain" />
                    : <div className="flex h-full w-full items-center justify-center text-[9px] text-subtle">No photo</div>}
                </div>
                <div className="mt-1 truncate text-[11px] font-medium text-text/90" title={c.name}>{c.name}</div>
                <div className="truncate font-mono text-[9px] text-subtle">{c.styleNumber}{c.brand ? ` · ${c.brand}` : ''}</div>
                <div className="mt-1">
                  <LocationChips locations={c.locations} empty="No logo location" />
                </div>
              </button>
              <button
                type="button"
                onClick={() => onQuickAdd(c.styleNumber)}
                disabled={added}
                className="mt-1.5 w-full rounded-sm bg-primary px-2 py-1 text-[11px] font-medium text-white disabled:bg-bg disabled:text-muted"
              >
                {added ? 'In lineup' : 'Add to lineup'}
              </button>
            </div>
          );
        })}
        {!visible.length && (
          <div className="col-span-full py-6 text-center text-[11px] text-muted">
            {cards.length && hideAdded ? 'Every matching garment is already in this lineup.' : 'No catalog garments match.'}
          </div>
        )}
      </div>

      {selected && (
        <div className="mb-3 grid gap-3 rounded-md border border-border bg-white p-3 sm:grid-cols-[140px_1fr]">
          <GarmentThumb src={selected.image} alt={selected.name} className="h-[140px] w-full" />
          <div className="space-y-2">
            <div>
              <div className="text-[14px] font-semibold tracking-tight">{selected.name}</div>
              <div className="text-[11px] text-muted">
                {selected.productType || selected.category}{selected.brand ? ` · ${selected.brand}` : ''}{selected.decoration ? ` · ${selected.decoration.toUpperCase()}` : ''}
              </div>
            </div>
            <div>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.04em] text-subtle">Logo locations</div>
              <LocationChips locations={selected.locations} />
              <div className="mt-1 text-[10px] text-subtle">
                Approved print areas on this blank. Which design sits where is set on Assign to Products — this lineup only picks the garment.
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <Fact label="Style numbers" value={selected.aliasLabels.join(' · ')} />
              <Fact label="Sizes" value={selected.sizes} />
              <Fact label="Price tiers" value={selected.tiers} />
              <Fact label="Fabric" value={selected.fabric} />
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <input
          list="product-lineup-style-numbers"
          value={typedStyle}
          onChange={(e) => onTypedStyle(e.target.value)}
          placeholder="or type a style # (e.g. GSF101)"
          className="h-7 w-44 rounded-sm border border-border bg-white px-1.5 font-mono text-[11px]"
        />
        <datalist id="product-lineup-style-numbers">
          {[...new Set(cards.flatMap((c) => c.aliases))].map((sn) => <option key={sn} value={sn} />)}
        </datalist>
        {COLOR_RULES.map((c) => (
          <label key={c} className="flex items-center gap-1 text-[11px]">
            <input
              type="checkbox"
              checked={!!newColors[c]}
              onChange={(e) => onColors({ ...newColors, [c]: e.target.checked })}
            />
            {c}
          </label>
        ))}
        <button
          onClick={onAdd}
          disabled={!canAdd || alreadyIn}
          title={alreadyIn ? `${selectedStyle} is already in this lineup` : addDisabledReason}
          className="rounded-sm border border-border bg-white px-2 py-1 text-[11px] hover:bg-bg disabled:opacity-50"
        >
          {alreadyIn ? 'Already added' : '+ add with these colors'}
        </button>
      </div>
      <div className="mt-1 text-[10px] text-subtle">
        Color-match expands each style against the school&apos;s colors. Click <b>Add to lineup</b> on a card to add it with black + primary, or check different rules here first.
      </div>
    </div>
  );
}
